import { useMemo, useRef, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TSTTable } from "@impero-com/ui";
import { Control, useFieldArray, UseFormTrigger } from "react-hook-form";

import { EntityView } from "api/generated/model";
import {
    CONTROL_ENTITIES_KEY,
    ControlProgramFormSchema,
} from "org/control/controlProgram/view/useControlProgramViewFormSchema";
import { useEntitiesQuery } from "org/entities/queries/useEntitiesQuery";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";

import { AddEntitiesTable } from "./AddEntitiesTable";
import * as theme from "./ControlEntitiesTables.css";
import { useLinkedEntitiesTable } from "./useLinkedEntitiesTable";
import { EntityTableRow } from "./utils";

interface LinkedEntitiesTableProps {
    control: Control<ControlProgramFormSchema>;
    trigger: UseFormTrigger<ControlProgramFormSchema>;
    canEditEntities: boolean;
    linkedEntities: EntityView[];
}

export function LinkedEntitiesTable({
    control,
    trigger,
    canEditEntities,
    linkedEntities,
}: LinkedEntitiesTableProps) {
    const [addEntitiesOpen, setAddEntitiesOpen] = useState(false);
    const { t } = useTranslation();
    const {
        fields: entityIdsFields,
        remove,
        append,
    } = useFieldArray({ control, name: CONTROL_ENTITIES_KEY });

    const { data } = useEntitiesQuery({ all: false, scope: "control" });
    const entityResource = useMemo(
        () => mapAsResourceWithoutAction(data.content),
        [data]
    );

    const controlEntitiesInForm: EntityTableRow[] = useMemo(
        () =>
            entityIdsFields.map((entityField) => {
                // First try the entity query (has full access rights info)
                const entityFromQuery = entityResource.find(
                    (entity) => entity.id === entityField.entityId
                );
                if (entityFromQuery) {
                    return {
                        entityId: entityFromQuery.id,
                        name: entityFromQuery.name,
                        tagIds: entityFromQuery.tagIds,
                        controlAccessRights:
                            entityFromQuery.controlAccessRights,
                    };
                }

                // Fall back to linked resources from control program response
                // (covers entities the user doesn't have direct access to)
                const entityFromLinkedResources = linkedEntities.find(
                    (entity) => entity.id === entityField.entityId
                );
                if (entityFromLinkedResources) {
                    return {
                        entityId: entityFromLinkedResources.id,
                        name: entityFromLinkedResources.name,
                        tagIds: entityFromLinkedResources.tagIds,
                        controlAccessRights: {
                            admin: [],
                            edit: [],
                            view: [],
                        },
                    };
                }

                throw new Error(
                    `Entity with id ${entityField.entityId} not found`
                );
            }),
        [entityIdsFields, entityResource, linkedEntities]
    );

    const handleRemoveLinkedEntity = (entityId: number) => {
        const index = entityIdsFields.findIndex(
            (entity) => entity.entityId === entityId
        );
        if (index !== -1) {
            remove(index);
            trigger(CONTROL_ENTITIES_KEY);
        }
    };

    const table = useLinkedEntitiesTable(
        controlEntitiesInForm,
        data.linkedResources,
        setAddEntitiesOpen,
        handleRemoveLinkedEntity,
        !canEditEntities
    );

    const filteredAvailableEntities = useMemo(
        () =>
            entityResource.filter(
                (entity) =>
                    !controlEntitiesInForm.some((e) => e.entityId === entity.id)
            ),
        [controlEntitiesInForm, entityResource]
    );

    const tableRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
            />
            {canEditEntities && (
                <>
                    <button
                        type="button"
                        onClick={() => setAddEntitiesOpen(true)}
                        className={theme.footerAction}
                    >
                        <PlusOutlined />
                        {t("EditControlProgram_Action_Add_Entities")}
                    </button>
                    <AddEntitiesTable
                        linkedResources={data.linkedResources}
                        open={addEntitiesOpen}
                        onClose={() => setAddEntitiesOpen(false)}
                        controlEntities={filteredAvailableEntities}
                        append={append}
                        trigger={trigger}
                    />
                </>
            )}
        </>
    );
}
